import { useState } from "react";
import { Share, SquarePlus, X, Smartphone } from "lucide-react";
import { lesen, schreiben } from "@/lib/speicher";
import { istInstalliert, istApple } from "@/lib/pwa";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer";

const MARKE = "app_hinweis_weg";

/**
 * "Als App auf den Home-Bildschirm" - der Hinweis fuer alle, die das CRM im
 * Safari-Tab benutzen.
 *
 * Auf dem iPhone gibt es keinen Installieren-Knopf; der Weg fuehrt ueber
 * Teilen -> Zum Home-Bildschirm. Ohne Hinweis findet ihn niemand, und ohne
 * Installation gibt es weder Vollbild noch Meldungen. Der Hinweis laesst
 * sich wegtippen und kommt dann nicht wieder (localStorage); wer die App
 * spaeter doch will, findet die Anleitung unter Mehr -> Meldungen.
 */
export function InstallAnleitung({ offen, onOffen }) {
  const apple = istApple();
  return (
    <Drawer open={offen} onOpenChange={onOffen}>
      <DrawerContent className="pb-[calc(1.5rem+env(safe-area-inset-bottom))]" data-testid="install-anleitung">
        <div className="px-5 pt-2">
          <DrawerTitle className="text-base">Als App auf den Home-Bildschirm</DrawerTitle>
          <DrawerDescription className="text-xs mt-0.5">
            Dann öffnet sich das CRM im Vollbild, mit eigenem Symbol - und kann Meldungen schicken.
          </DrawerDescription>
        </div>
        <ol className="px-5 mt-4 space-y-3 text-sm text-slate-800">
          {apple ? (
            <>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Unten in Safari auf <b>Teilen</b> <Share className="inline h-4 w-4 -mt-0.5" /> tippen.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>In der Liste <b>Zum Home-Bildschirm</b> <SquarePlus className="inline h-4 w-4 -mt-0.5" /> wählen, dann <b>Hinzufügen</b>.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">3</span>
                <span>Die App vom Home-Bildschirm öffnen und einmal anmelden. Unter <b>Mehr → Meldungen</b> die Meldungen einschalten.</span>
              </li>
            </>
          ) : (
            <>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">1</span>
                <span>Im Browser-Menü (drei Punkte) <b>App installieren</b> oder <b>Zum Startbildschirm hinzufügen</b> wählen.</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="h-7 w-7 rounded-full bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">2</span>
                <span>Die App öffnen und unter <b>Mehr → Meldungen</b> die Meldungen einschalten.</span>
              </li>
            </>
          )}
        </ol>
        <p className="px-5 mt-4 text-xs text-slate-500">
          Es ist dieselbe Adresse wie im Browser. Jede neue Fassung des CRM kommt automatisch in die App.
        </p>
      </DrawerContent>
    </Drawer>
  );
}

export default function AppInstallieren() {
  const [weg, setWeg] = useState(() => lesen(MARKE) === "1");
  const [anleitung, setAnleitung] = useState(false);
  if (weg || istInstalliert()) return null;
  return (
    <>
      <div className="md:hidden mx-3 mt-3 rounded-xl border border-sky-200 bg-sky-50 text-slate-900 px-3.5 py-2.5 flex items-center gap-3"
           data-testid="install-hinweis">
        <Smartphone className="h-5 w-5 shrink-0 text-[#148BCB]" />
        <button type="button" onClick={() => setAnleitung(true)} className="text-left flex-1 min-w-0">
          <span className="block text-sm font-semibold leading-tight">Als App auf den Home-Bildschirm</span>
          <span className="block text-[11px] text-slate-600 leading-tight mt-0.5">Vollbild und Meldungen - so geht's</span>
        </button>
        <button type="button" onClick={() => { schreiben(MARKE, "1"); setWeg(true); }} aria-label="Hinweis ausblenden"
                data-testid="install-hinweis-weg"
                className="h-8 w-8 rounded-md flex items-center justify-center text-slate-400 hover:text-slate-900">
          <X className="h-4 w-4" />
        </button>
      </div>
      <InstallAnleitung offen={anleitung} onOffen={setAnleitung} />
    </>
  );
}
